import EsfirusSelect from '@components/ui/Select/EsfirusSelect';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import { customersService } from '@services/customers';
import useDebounce from '@services/hooks/useDebounce';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import EsfirusButton from '../../../../components/ui/Button/EsfirusButton';
import EsfirusTextInput from '../../../../components/ui/Text-input/EsfirusTextInput';
import './ModalNewClient.scss';

interface Client {
  customer_id: string;
  business_name: string;
  company_id: number;
  vat_number: string;
}

interface ModalNewClientProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  selectClient: (client: Client) => Promise<void>;
}

function ModalNewClient({ open, setOpen, selectClient }: ModalNewClientProps) {
  const empresas = useSelector((state: any) => state.empresas.list);

  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [filterBusinessName, setFilterBusinessName] = useState('');
  const [filterCIF, setFilterCIF] = useState('');
  const [selectedEmpresa, setSelectedEmpresa] = useState<any>('');

  const [clientsList, setClientsList] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [currentClient, setCurrentClient] = useState<Client | null>(null);

  const inputRefs = useRef<{ [key: string]: HTMLInputElement }>({});

  const timeDebounce = 500;
  const debouncedCustomerId = useDebounce(filterCustomerId, timeDebounce);
  const debouncedBusinessName = useDebounce(filterBusinessName, timeDebounce);
  const debouncedCIFName = useDebounce(filterCIF, timeDebounce);
  const debouncedEmpresa = useDebounce(selectedEmpresa, timeDebounce);

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const response = await customersService.getFilteredCustomers({
        customerId: debouncedCustomerId,
        businessName: debouncedBusinessName,
        companyId: debouncedEmpresa || undefined,
        vatNumber: debouncedCIFName
      });

      setClientsList(response.data.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClientsList([]);
    }
    setLoadingClients(false);
  };

  useEffect(() => {
    if (open) fetchClients();
  }, [debouncedCustomerId, debouncedBusinessName, debouncedEmpresa, open, debouncedCIFName]);

  const acceptClose = async () => {
    if (currentClient) await selectClient(currentClient);
    setOpen(false);
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <div className="new-client-modal">
        <div className="header">
          <h5 className="text-center">Selecciona el cliente</h5>
        </div>

        <div className="body">
          <p className="text-center-mobile">
            Filtra el listado de clientes mediante los siguientes campos:
          </p>

          <Grid container spacing={4}>
            <Grid item md={6} xs={12}>
              <label>Id del Cliente</label>
              <EsfirusTextInput
                ref={(el: HTMLInputElement) => (inputRefs.current['customerId'] = el)}
                value={filterCustomerId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterCustomerId(e.target.value)}
                placeholder="Id del Cliente"
                fullWidth
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <label>Nombre</label>
              <EsfirusTextInput
                ref={(el: HTMLInputElement) => (inputRefs.current['businessName'] = el)}
                value={filterBusinessName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterBusinessName(e.target.value)}
                placeholder="Nombre"
                fullWidth
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <label>Empresa</label>
              <EsfirusSelect
                options={empresas.map((e: any) => ({ label: e.Nombre, value: e.Empresa }))}
                value={selectedEmpresa}
                change={setSelectedEmpresa}
                placeholder="Seleccionar empresa"
              />
            </Grid>

            <Grid item md={6} xs={12}>
              <label>CIF</label>
              <EsfirusTextInput
                ref={(el: HTMLInputElement) => (inputRefs.current['cif'] = el)}
                value={filterCIF}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterCIF(e.target.value)}
                placeholder="Nombre"
                fullWidth
              />
            </Grid>
          </Grid>

          <div className="client-container mt-20">
            <div className="scroll">
              {loadingClients ? (
                <div className="client-item">Cargando clientes...</div>
              ) : clientsList.length === 0 ? (
                <div className="client-item">No existe ningún cliente con los filtros aplicados.</div>
              ) : (
                clientsList.map((client, index) => (
                  <div
                    key={`${client.customer_id}-${index}`}
                    className={`client-item${currentClient?.customer_id === client.customer_id ? ' selected' : ''}`}
                    onClick={() => setCurrentClient(client)}
                    onDoubleClick={async () => {
                      setCurrentClient(client);
                      await acceptClose();
                    }}
                  >
                    {client.customer_id} - {client.business_name} (Empresa {client.company_id}) - {client.vat_number}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="button-container">
            <EsfirusButton onClick={acceptClose} fullWidth color="secondary">
              Aceptar
            </EsfirusButton>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export { ModalNewClient };
